// ─────────────────────────────────────────────────────────────────────────────
// Autovendas.jsx — Página "IMOBMEET Autovendas" (/autovendas), versão rica.
// Hero escuro com mockup animado da jornada de compra (6 passos, flat) e feed de
// transações; jornada em 6 passos; canais de venda; formas de pagamento; estoque
// sincronizado (tela real); pipeline da operação; FAQ (schema) e CTA.
// SEO (title/description/keywords) e FAQ mantidos da versão anterior.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { C, font } from "../tokens";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FaqSection from "../components/FaqSection";
import { applySeo } from "../lib/seo";
import { Reveal, TileIcon, Stage } from "./FuncionalidadeDetalhe";
import "../styles/blog.css";

const ACCENT = "#EC4899";
const BG = "linear-gradient(135deg, #EC4899 0%, #BE185D 100%)";

// ─── Dados ───────────────────────────────────────────────────────────────────
const STEPS = [
  { icon: "users", title: "Cadastro inicial", desc: "O cliente acessa o link e cria a conta com poucos dados." },
  { icon: "doc", title: "Envio de documentos", desc: "Upload de RG, CPF e comprovantes direto pelo celular." },
  { icon: "kanban", title: "Escolha da unidade", desc: "Seleciona a unidade disponível no estoque, em tempo real." },
  { icon: "money", title: "Forma de pagamento", desc: "Define entrada, parcelas e financiamento com simulação." },
  { icon: "bolt", title: "Envio da proposta", desc: "A proposta é gerada e registrada no CRM automaticamente." },
  { icon: "shield", title: "Pagamento", desc: "Pix, boleto ou cartão de crédito — pagamento na hora." },
];

const CHANNELS = [
  { tag: "Venda direta", icon: "map", title: "A incorporadora vende direto ao cliente",
    desc: "Publique o link oficial de vendas no seu site, anúncios e redes. O cliente compra sozinho, do cadastro ao pagamento, sem intermediário e sem depender de horário comercial.",
    bullets: ["Link oficial da incorporadora ou loteadora", "Venda 24 horas por dia, 7 dias por semana", "Estoque e disponibilidade sempre atualizados"] },
  { tag: "Link do corretor", icon: "users", title: "O corretor gera o link e a venda cai para ele",
    desc: "Cada corretor cria o seu próprio link e envia para o cliente. Quando a venda acontece, ela é atribuída automaticamente ao corretor, gerando o comissionamento correto — sem disputa e sem planilha.",
    bullets: ["Link individual por corretor", "Atribuição automática da venda e da comissão", "Split de comissões integrado à pagadoria"] },
];

const PAYMENTS = [
  { name: "Pix", desc: "Aprovação na hora", icon: "bolt", tone: "#10B981" },
  { name: "Boleto", desc: "Emissão automática", icon: "money", tone: "#0560FF" },
  { name: "Cartão de crédito", desc: "Em até 12x", icon: "shield", tone: "#7C3AED" },
];

const OPS = [
  { icon: "chat", title: "Publique o link", text: "Site, anúncios, redes — ou o link individual de cada corretor." },
  { icon: "users", title: "Cliente compra sozinho", text: "Cadastro, documentos, unidade, condição e pagamento." },
  { icon: "kanban", title: "Venda cai no CRM", text: "Lead, proposta e contrato registrados automaticamente." },
  { icon: "money", title: "Recebíveis gerados", text: "Pix, boleto ou cartão entram na gestão de recebíveis." },
  { icon: "shuffle", title: "Comissão atribuída", text: "Split calculado e pago pela pagadoria integrada." },
];

const FAQ = [
  { q: "O cliente consegue comprar sozinho, sem corretor?", a: "Sim. Pelo link oficial da incorporadora ou loteadora, o cliente percorre todo o passo a passo — cadastro, documentos, escolha da unidade, condição de pagamento e pagamento — sem precisar de atendimento humano. Ainda assim, o seu time acompanha tudo em tempo real pelo CRM." },
  { q: "Como funciona o link do corretor?", a: "Cada corretor gera o próprio link de vendas e envia ao cliente. Quando a compra é concluída, a venda é atribuída automaticamente àquele corretor, garantindo o comissionamento correto e o split da comissão." },
  { q: "Quais formas de pagamento são aceitas?", a: "Pix (com aprovação na hora), boleto bancário (emitido automaticamente) e cartão de crédito, com parcelamento. Tudo integrado à gestão de recebíveis do IMOBMEET." },
  { q: "A disponibilidade de unidades fica sincronizada?", a: "Sim. O estoque exibido no Autovendas é o mesmo do CRM. Assim que uma unidade é vendida ou reservada, ela deixa de aparecer para os demais compradores, evitando venda em duplicidade." },
];

// Feed de transações (mockup): linhas aparecem em sequência no loop de 18s
const FEED = [
  { icon: "bolt", tone: "#10B981", title: "Pix aprovado", sub: "Entrada · Unidade 2502", amount: "R$ 25.000,00", status: "Aprovado" },
  { icon: "money", tone: "#0560FF", title: "Boleto emitido", sub: "Parcela 1/60 · Unidade 2502", amount: "R$ 3.522,54", status: "Emitido" },
  { icon: "shield", tone: "#7C3AED", title: "Cartão de crédito", sub: "Sinal em 12x · Quadra 10 · Lote 04", amount: "R$ 12.000,00", status: "Aprovado" },
  { icon: "kanban", tone: "#F59E0B", title: "Estoque atualizado", sub: "Unidade 2502 marcada como vendida", amount: "", status: "Sincronizado" },
  { icon: "users", tone: "#EC4899", title: "Comissão atribuída", sub: "Venda do link do corretor Marcos R.", amount: "", status: "No split" },
];

// Mini-cenas animadas dos cards da jornada (loop de 4s, defasado por card via --d)
const JOURNEY_VIS = [
  () => (
    <div className="jc-form">
      <span className="jc-line t1" style={{ "--w": "62%" }} />
      <span className="jc-line t2" style={{ "--w": "84%" }} />
      <span className="jc-line t3" style={{ "--w": "48%" }} />
      <span className="jc-cta">Criar conta</span>
    </div>
  ),
  () => (
    <div className="jc-docs">
      {["RG / CNH", "Comprovante de residência", "Comprovante de renda"].map((d, i) => (
        <span key={d} className="jc-docrow" style={{ animationDelay: `calc(var(--d) + ${i * 0.55}s)` }}><i /><span>{d}</span><b>✓</b></span>
      ))}
    </div>
  ),
  () => (
    <div className="jc-units">
      {["2501", "2502", "2503", "2504", "2505", "2506", "2507", "2508"].map((u, i) => (
        <span key={u} className={`jc-unit ${i === 1 ? "sel" : i === 3 || i === 6 ? "sold" : ""}`}>{u}</span>
      ))}
    </div>
  ),
  () => (
    <div className="jc-pay-vis">
      <div className="jc-bars">
        {[38, 62, 30, 78, 50, 66].map((h, i) => <i key={i} style={{ "--h": `${h}%`, animationDelay: `calc(var(--d) + ${i * 0.12}s)` }} />)}
      </div>
      <span className="jc-pay-lbl">Entrada + 60x · simulação</span>
    </div>
  ),
  () => (
    <div className="jc-pdf">
      <i />
      <div><b>Proposta_2502.pdf</b><span>gerada e registrada no CRM</span></div>
      <em>Enviada</em>
    </div>
  ),
  () => (
    <div className="jc-paid">
      <span className="jc-check"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg></span>
      <b>Pix aprovado</b>
      <span>R$ 25.000,00 · na hora</span>
    </div>
  ),
];

const Check = ({ color = "#10B981" }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// ─── Mockup animado: jornada (stepper flat + card por etapa) + feed ──────────
function JourneyMock() {
  return (
    <div className="av-mock feat-window">
      <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
        </span>
        <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>Autovendas · vendas.suaincorporadora.com.br</span>
        <span style={{ width: 40 }} />
      </div>
      <div className="av-body" style={{ fontFamily: font }}>
        {/* Jornada */}
        <div className="av-journey">
          <div className="av-stepper">
            <span className="av-track"><i className="av-prog" /></span>
            {STEPS.map((s, i) => (
              <span key={i} className={`av-dot d${i + 1}`}><TileIcon name={s.icon} /></span>
            ))}
          </div>
          <div className="av-panels">
            {/* 1 Cadastro */}
            <div className="av-panel p1">
              <div className="av-ph">Novo cadastro</div>
              <div className="av-field"><span>Nome completo</span><b>Ana Paula Ribeiro</b></div>
              <div className="av-field"><span>CPF</span><b>•••.•••.•••-42</b></div>
              <div className="av-field"><span>WhatsApp</span><b>(11) •••••-8841</b></div>
              <div className="av-btn">Criar conta</div>
            </div>
            {/* 2 Documentos */}
            <div className="av-panel p2">
              <div className="av-ph">Envio de documentos</div>
              {["RG ou CNH · frente e verso", "Comprovante de residência", "Comprovante de renda"].map((d, i) => (
                <div key={d} className="av-doc" style={{ animationDelay: `${3 + i * 0.6}s` }}><i /><span>{d}</span><b>enviado</b></div>
              ))}
            </div>
            {/* 3 Unidade */}
            <div className="av-panel p3">
              <div className="av-ph">Escolha da unidade · Torre 1</div>
              <div className="av-grid">
                {["2501", "2502", "2503", "2504", "2505", "2506", "2507", "2508"].map((u, i) => (
                  <span key={u} className={`av-unit ${i === 1 ? "sel" : i === 3 || i === 6 ? "sold" : ""}`}>{u}</span>
                ))}
              </div>
              <div className="av-note">Unidade 2502 · 68 m² · disponível</div>
            </div>
            {/* 4 Pagamento */}
            <div className="av-panel p4">
              <div className="av-ph">Forma de pagamento</div>
              <div className="av-field"><span>Tabela</span><b>Lançamento · Entrada + 60x</b></div>
              <div className="av-field"><span>Entrada (Pix)</span><b>R$ 25.000,00</b></div>
              <div className="av-field"><span>60 parcelas</span><b>R$ 3.522,54</b></div>
              <div className="av-btn">Confirmar condição</div>
            </div>
            {/* 5 Proposta */}
            <div className="av-panel p5">
              <div className="av-ph">Proposta enviada</div>
              <div className="av-pdf"><i /><div><b>Proposta_2502.pdf</b><span>registrada no CRM · aguardando pagamento</span></div></div>
              <div className="av-note ok">Proposta aprovada — siga para o pagamento</div>
            </div>
            {/* 6 Pagamento aprovado */}
            <div className="av-panel p6">
              <div className="av-pay">
                <span className="av-pay-ic"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg></span>
                <b>Pagamento aprovado via Pix</b>
                <span>R$ 25.000,00 · Unidade 2502 reservada para você</span>
              </div>
            </div>
          </div>
        </div>
        {/* Feed de transações */}
        <div className="av-feed">
          <div className="av-feed-h"><span className="live" /> Transações em tempo real</div>
          {FEED.map((f, i) => (
            <div key={i} className="av-row" style={{ animationDelay: `${2.5 + i * 2.9}s` }}>
              <span className="av-row-ic" style={{ background: f.tone }}><TileIcon name={f.icon} /></span>
              <div className="av-row-t"><b>{f.title}</b><span>{f.sub}</span></div>
              <div className="av-row-r">{f.amount && <b>{f.amount}</b>}<span style={{ color: f.tone, borderColor: `${f.tone}55`, background: `${f.tone}14` }}>{f.status}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────
export default function Autovendas() {
  useEffect(() => {
    applySeo({
      title: "IMOBMEET Autovendas: Venda de Unidades 100% Online | IMOBMEET",
      description: "Venda de unidades 100% online: o cliente acessa um link e conclui a compra sozinho — cadastro, documentos, escolha da unidade, proposta e pagamento.",
      keywords: "venda de imóveis online, autovendas, venda de unidades online, estande online, pagamento Pix imóvel, venda de lotes online, checkout imobiliário",
      path: "/autovendas",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar activeSolution="/autovendas" />

      {/* ── Hero ── */}
      <section className="feat-hero" style={{ background: "linear-gradient(135deg, #0B1733 0%, #0A1230 100%)", padding: "108px 28px 160px" }}>
        <div className="feat-gridanim" />
        <div className="feat-orb" style={{ width: 520, height: 520, background: C.accent, top: -180, left: -140 }} />
        <div className="feat-orb b" style={{ width: 460, height: 460, background: ACCENT, top: -120, right: -120 }} />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", textAlign: "center" }}>
          <div className="feat-rise" style={{ animationDelay: "0.05s" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: font, fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#fff", background: BG, padding: "6px 14px 6px 8px", borderRadius: 999, marginBottom: 22, boxShadow: `0 10px 30px ${ACCENT}55` }}>
              <span style={{ width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><TileIcon name="bolt" /></span>
              IMOBMEET Autovendas
            </span>
          </div>
          <h1 className="feat-rise" style={{ fontFamily: font, fontSize: "clamp(32px, 5vw, 52px)", fontWeight: 800, lineHeight: 1.04, letterSpacing: -1.4, color: "#fff", margin: "0 0 18px", animationDelay: "0.15s" }}>
            Venda de unidades 100% online, <span style={{ background: "linear-gradient(90deg, #F9A8D4, #7DD3FC)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>do cadastro ao pagamento</span>
          </h1>
          <p className="feat-rise" style={{ fontFamily: font, fontSize: 18.5, lineHeight: 1.55, color: "rgba(255,255,255,0.82)", margin: "0 auto 30px", maxWidth: 720, animationDelay: "0.25s" }}>
            O cliente acessa um link e conclui a compra sozinho — cadastro, documentos, escolha da unidade, proposta e pagamento via Pix, boleto ou cartão. Sua incorporadora ou loteadora vende 24 horas por dia.
          </p>
          <div className="feat-rise" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", animationDelay: "0.35s" }}>
            <Link to="/agendar-demo" style={{ fontFamily: font, fontSize: 15, fontWeight: 800, color: "#fff", background: C.accent, padding: "14px 26px", borderRadius: 12, textDecoration: "none", boxShadow: "0 10px 28px rgba(5,96,255,0.45)" }}>
              Agendar demonstração →
            </Link>
            <a href="#jornada" style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.08)", border: "1.5px solid rgba(255,255,255,0.26)", padding: "14px 26px", borderRadius: 12, textDecoration: "none", backdropFilter: "blur(6px)" }}>
              Ver a jornada de compra
            </a>
          </div>
        </div>
      </section>

      {/* ── Palco: mockup da jornada + feed, sobreposto ao hero ── */}
      <section style={{ background: C.surface, padding: "0 28px" }}>
        <div className="feat-rise" style={{ maxWidth: 1000, margin: "-124px auto 0", position: "relative", zIndex: 2, animationDelay: "0.45s" }}>
          <Stage
            glow={`radial-gradient(60% 60% at 50% 50%, ${ACCENT} 0%, ${C.accent} 55%, transparent 100%)`}
            chips={[
              { label: "Pix aprovado · R$ 25.000,00", pos: "tl", tone: "success" },
              { label: "Unidade 2502 bloqueada no estoque ✓", pos: "tr", tone: "success" },
              { label: "Venda pelo link do corretor", pos: "bl", icon: "avatar", initials: "MR" },
              { label: "Cliente comprando sozinho, 24h", pos: "br", icon: "kanban" },
            ]}
          >
            <JourneyMock />
          </Stage>
        </div>
        <div style={{ display: "flex", gap: 36, justifyContent: "center", flexWrap: "wrap", padding: "44px 0 0" }}>
          {[["6", "passos até a compra"], ["24h", "vendendo por dia"], ["Pix · Boleto · Cartão", "formas de pagamento"]].map(([v, l]) => (
            <div key={l} style={{ fontFamily: font, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: -0.5 }}>{v}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Jornada em 6 passos (flat) ── */}
      <section id="jornada" style={{ background: C.surface, padding: "80px 28px 64px", scrollMarginTop: 80 }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <Reveal style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 44px" }}>
            <h2 style={{ fontFamily: font, fontSize: "clamp(26px,3.4vw,38px)", fontWeight: 800, letterSpacing: -1, color: C.text, margin: "0 0 12px" }}>
              A jornada de compra em 6 passos
            </h2>
            <p style={{ fontFamily: font, fontSize: 17, lineHeight: 1.65, color: C.muted, margin: 0 }}>
              Do primeiro clique ao pagamento aprovado — tudo pelo celular do cliente, com a venda registrada no CRM.
            </p>
          </Reveal>
          <div className="jc-grid">
            {STEPS.map((s, i) => {
              const Vis = JOURNEY_VIS[i];
              return (
                <Reveal key={i} delay={Math.min(i + 1, 6)} className="jc" style={{ "--d": `${i * 0.6}s` }}>
                  <span className="jc-bar" style={{ background: BG }} />
                  <span className="jc-num" style={{ fontFamily: font }}>{String(i + 1).padStart(2, "0")}</span>
                  <div className="jc-head">
                    <span className="jc-ic" style={{ background: BG, boxShadow: `0 10px 24px ${ACCENT}55` }}><TileIcon name={s.icon} /></span>
                    <span className="jc-step" style={{ fontFamily: font, color: ACCENT }}>Passo {i + 1}</span>
                  </div>
                  <div className="jc-vis" style={{ fontFamily: font }}><Vis /></div>
                  <h3 style={{ fontFamily: font, fontSize: 18, fontWeight: 800, color: C.text, margin: "16px 0 6px", lineHeight: 1.2, letterSpacing: -0.3 }}>{s.title}</h3>
                  <p style={{ fontFamily: font, fontSize: 14.5, lineHeight: 1.55, color: C.muted, margin: 0 }}>{s.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Canais de venda ── */}
      <section style={{ background: C.bgAlt, padding: "76px 28px" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <Reveal style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 36px" }}>
            <h2 style={{ fontFamily: font, fontSize: "clamp(26px,3.4vw,36px)", fontWeight: 800, letterSpacing: -0.9, color: C.text, margin: "0 0 12px" }}>
              Dois canais de venda, um único estoque
            </h2>
            <p style={{ fontFamily: font, fontSize: 17, lineHeight: 1.65, color: C.muted, margin: 0 }}>
              A incorporadora vende sozinha e também dá autonomia para cada corretor gerar o seu link.
            </p>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 22 }}>
            {CHANNELS.map((c, i) => (
              <Reveal key={c.tag} delay={i + 1} className="feat-card" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 22, padding: 30, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
                <span className="feat-tile-bar" style={{ background: BG, transform: "scaleX(1)" }} />
                <span style={{ width: 52, height: 52, borderRadius: 15, background: BG, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, boxShadow: `0 12px 28px ${ACCENT}55` }}>
                  <TileIcon name={c.icon} />
                </span>
                <span style={{ alignSelf: "flex-start", fontFamily: font, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: ACCENT, background: `${ACCENT}14`, padding: "5px 12px", borderRadius: 999, marginBottom: 12 }}>{c.tag}</span>
                <h3 style={{ fontFamily: font, fontSize: 22, fontWeight: 800, letterSpacing: -0.5, color: C.text, margin: "0 0 12px", lineHeight: 1.25 }}>{c.title}</h3>
                <p style={{ fontFamily: font, fontSize: 15.5, lineHeight: 1.65, color: C.muted, margin: "0 0 18px" }}>{c.desc}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: "auto" }}>
                  {c.bullets.map((b) => (
                    <div key={b} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <Check />
                      <span style={{ fontFamily: font, fontSize: 14.5, lineHeight: 1.5, color: C.text, fontWeight: 500 }}>{b}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pagamento ── */}
      <section style={{ background: C.surface, padding: "76px 28px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <Reveal style={{ textAlign: "center", maxWidth: 680, margin: "0 auto 36px" }}>
            <h2 style={{ fontFamily: font, fontSize: "clamp(26px,3.4vw,36px)", fontWeight: 800, letterSpacing: -0.9, color: C.text, margin: "0 0 12px" }}>
              Pagamento na hora, do jeito do cliente
            </h2>
            <p style={{ fontFamily: font, fontSize: 17, lineHeight: 1.65, color: C.muted, margin: 0 }}>
              Três formas de pagamento integradas à gestão de recebíveis do IMOBMEET.
            </p>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
            {PAYMENTS.map((p, i) => (
              <Reveal key={p.name} delay={i + 1} className="feat-card" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, padding: "24px 22px", display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0, background: `${p.tone}14`, color: p.tone, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TileIcon name={p.icon} />
                </span>
                <div>
                  <div style={{ fontFamily: font, fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>{p.name}</div>
                  <div style={{ fontFamily: font, fontSize: 13.5, color: C.muted, marginTop: 2 }}>{p.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal style={{ textAlign: "center", marginTop: 22 }}>
            <Link to="/gestao-de-recebiveis" style={{ fontFamily: font, fontSize: 14, fontWeight: 800, color: C.accent, textDecoration: "none" }}>
              Ver a gestão de recebíveis →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── Estoque sincronizado ── */}
      <section style={{ background: C.bgAlt, padding: "76px 28px" }}>
        <div className="feat-block" style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56 }}>
          <Reveal>
            <span style={{ display: "inline-block", fontFamily: font, fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: ACCENT, marginBottom: 12 }}>ESTOQUE ÚNICO</span>
            <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,32px)", fontWeight: 800, letterSpacing: -0.8, color: C.text, margin: "0 0 14px", lineHeight: 1.12 }}>
              A unidade vendida online some do estoque na hora
            </h2>
            <p style={{ fontFamily: font, fontSize: 16.5, lineHeight: 1.7, color: "#3A4A63", margin: "0 0 18px" }}>
              O estoque do Autovendas é o mesmo do CRM. Pagou, a unidade é bloqueada para todo mundo — corretores, site e demais compradores — sem venda em duplicidade.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {["Espelho e mapa humanizado atualizados em tempo real", "Reserva automática enquanto o pagamento é confirmado", "Venda, proposta e contrato registrados no lead"].map((b) => (
                <div key={b} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <Check />
                  <span style={{ fontFamily: font, fontSize: 15, lineHeight: 1.5, color: C.text, fontWeight: 500 }}>{b}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={2} className="feat-media">
            <div className="feat-stage">
              <div className="feat-glow" style={{ background: `radial-gradient(60% 60% at 50% 50%, ${ACCENT} 0%, ${C.accent} 55%, transparent 100%)` }} />
              <div className="feat-tilt">
                <div className="feat-window">
                  <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
                    <span style={{ display: "flex", gap: 6 }}><i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} /></span>
                    <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>Espelho de vendas · Torre 1</span>
                    <span style={{ width: 40 }} />
                  </div>
                  <img src="/assets/sys/espelho-vertical.webp" alt="Espelho de vendas com a unidade vendida bloqueada" loading="lazy" decoding="async" />
                </div>
              </div>
              <span className="feat-chip tr" style={{ fontFamily: font, animationDelay: "0.5s, 0s" }}><span className="dot" style={{ background: "#10B981" }} />Unidade 2502 vendida no Autovendas</span>
              <span className="feat-chip bl" style={{ fontFamily: font, animationDelay: "0.9s, 0.7s" }}><span className="dot" style={{ background: C.accent }} />Bloqueada para os demais ✓</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Como funciona (operação) ── */}
      <section style={{ background: C.surface, padding: "56px 28px 84px" }}>
        <Reveal className="feat-pipe-wrap" style={{ maxWidth: 1160, margin: "0 auto" }}>
          <div className="feat-gridanim" />
          <div className="feat-orb" style={{ width: 420, height: 420, background: C.accent, top: -200, left: -120, opacity: .45 }} />
          <div className="feat-orb b" style={{ width: 380, height: 380, background: ACCENT, bottom: -220, right: -100, opacity: .35 }} />
          <div style={{ position: "relative", textAlign: "center", marginBottom: 44 }}>
            <span style={{ display: "inline-block", fontFamily: font, fontSize: 11.5, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "#9DBBFF", background: "rgba(5,96,255,0.18)", border: "1px solid rgba(157,187,255,0.25)", padding: "6px 12px", borderRadius: 999, marginBottom: 14 }}>
              Do lado da operação
            </span>
            <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,34px)", fontWeight: 800, letterSpacing: -0.8, color: "#fff", margin: 0 }}>
              Como funciona para a incorporadora
            </h2>
          </div>
          <div className="feat-pipe dark" style={{ gridTemplateColumns: `repeat(${OPS.length}, 1fr)` }}>
            <div className="feat-pipe-line" style={{ left: `${100 / (2 * OPS.length)}%`, right: `${100 / (2 * OPS.length)}%` }}><span className="feat-pipe-glow" /></div>
            {OPS.map((s, i) => (
              <div key={i} className="feat-pipe-card" style={{ textAlign: "center" }}>
                <div className="feat-pipe-dot" style={{ background: BG, fontFamily: font, animationDelay: `${i * 0.45}s` }}>{i + 1}</div>
                <div className="feat-glass">
                  <span className="feat-glass-num" style={{ fontFamily: font }}>{String(i + 1).padStart(2, "0")}</span>
                  <span style={{ width: 44, height: 44, borderRadius: 13, background: BG, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14, boxShadow: `0 10px 26px ${ACCENT}66` }}>
                    <TileIcon name={s.icon} />
                  </span>
                  <h3 style={{ fontFamily: font, fontSize: 16, fontWeight: 800, color: "#fff", margin: "0 0 6px", lineHeight: 1.25 }}>{s.title}</h3>
                  <p style={{ fontFamily: font, fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.72)", margin: 0 }}>{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ── FAQ ── */}
      <FaqSection items={FAQ} title="Perguntas frequentes sobre o Autovendas" />

      {/* ── CTA ── */}
      <section style={{ background: C.bgAlt, padding: "0 28px 72px" }}>
        <Reveal className="feat-cta" style={{ maxWidth: 1160, margin: "0 auto", padding: "50px 40px", borderRadius: 24, background: "linear-gradient(135deg, #0560FF 0%, #204493 100%)", color: "#fff", textAlign: "center" }}>
          <div className="feat-orb" style={{ width: 320, height: 320, background: ACCENT, top: -160, right: -80, opacity: .4 }} />
          <div style={{ position: "relative" }}>
            <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,30px)", fontWeight: 800, letterSpacing: -0.7, margin: "0 0 12px" }}>
              Coloque suas vendas no piloto automático
            </h2>
            <p style={{ fontFamily: font, fontSize: 16.5, lineHeight: 1.6, color: "rgba(255,255,255,0.85)", margin: "0 auto 26px", maxWidth: 560 }}>
              Agende uma demonstração e veja o Autovendas do IMOBMEET aplicado ao seu empreendimento.
            </p>
            <Link to="/agendar-demo" className="feat-card" style={{ display: "inline-flex", fontFamily: font, fontSize: 15, fontWeight: 800, color: C.accent, background: "#fff", padding: "15px 32px", borderRadius: 12, textDecoration: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              Agendar demonstração →
            </Link>
          </div>
        </Reveal>
      </section>

      <Footer />
    </>
  );
}
